import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConditionBuilder } from '../condition-builder/ConditionBuilder';
import { useKeyboardSave } from './use-keyboard-save';
import type { ObjectRuleElement, Condition } from '@/types/policy-editor';

interface ObjectRuleFormProps {
    element: ObjectRuleElement | null;
    onSave: (element: ObjectRuleElement) => void;
    onCancel: () => void;
}

export function ObjectRuleForm({ element, onSave, onCancel }: ObjectRuleFormProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [keyExpression, setKeyExpression] = useState('');
    const [valueExpression, setValueExpression] = useState('');
    const [conditions, setConditions] = useState<Condition[]>([]);

    useEffect(() => {
        if (element) {
            setName(element.name);
            setDescription(element.description ?? '');
            setKeyExpression(element.keyExpression);
            setValueExpression(element.valueExpression);
            setConditions(element.conditions);
        } else {
            setName(''); setDescription(''); setKeyExpression(''); setValueExpression(''); setConditions([]);
        }
    }, [element]);

    const handleSave = useCallback(() => {
        if (!name.trim() || !keyExpression.trim() || !valueExpression.trim()) return;
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'objectRule',
            name,
            description: description || undefined,
            keyExpression,
            valueExpression,
            conditions,
        });
    }, [element, name, description, keyExpression, valueExpression, conditions, onSave]);

    useKeyboardSave(handleSave, !!name.trim() && !!keyExpression.trim() && !!valueExpression.trim());

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    An object rule builds key-value pairs. Each time the conditions are true,
                    the key and value are added to the object.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="obj-name">Object name</Label>
                        <Input id="obj-name" placeholder="e.g. users_by_role, task_models" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="obj-desc">Description</Label>
                        <Input id="obj-desc" placeholder="What this object maps" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="obj-key">Key expression</Label>
                        <Input id="obj-key" className="font-mono text-xs" placeholder="e.g. role, model_name" value={keyExpression} onChange={(e) => setKeyExpression(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="obj-value">Value expression</Label>
                        <Input id="obj-value" className="font-mono text-xs" placeholder="e.g. user, model_config" value={valueExpression} onChange={(e) => setValueExpression(e.target.value)} />
                    </div>
                </div>

                <ConditionBuilder conditions={conditions} onChange={setConditions} />
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave} disabled={!name.trim() || !keyExpression.trim() || !valueExpression.trim()}>Save</Button>
                </div>
            </div>
        </div>
    );
}
